<template>
  <div class="pgs-dashboard">
    <!-- Header Section -->
    <div class="d-flex align-center justify-space-between mb-8">
      <div>
        <h1 class="text-h4 font-weight-bold pgs-text-primary mb-1">Dashboard</h1>
        <p class="text-subtitle-1 pgs-text-secondary mb-0">Resumen de operaciones del día: {{ currentDate }}</p>
      </div>
      <div class="d-flex align-center">
        <!-- Weather Widget (Simple) -->
        <div v-if="weather" class="pgs-weather-pill mr-4 d-flex align-center px-4 py-2 rounded-pill">
          <v-icon :color="weatherIconColor" size="24" class="mr-2">{{ weatherIcon }}</v-icon>
          <div>
            <div class="font-weight-bold pgs-text-primary text-body-2" style="line-height: 1;">{{ weather.temperature
            }}°C</div>
            <div class="text-caption pgs-text-secondary" style="line-height: 1.2;">{{ weatherDescription }}</div>
          </div>
        </div>

      </div>
    </div>

    <!-- Tabs de Categorías -->
    <v-tabs v-model="activeTab" class="mb-4" background-color="transparent" color="primary">
      <v-tab href="#golf">Golf</v-tab>
      <v-tab href="#actividades">Actividades</v-tab>
      <v-tab href="#ab">A&B</v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeTab" style="background-color: transparent;">
      <!-- Tab Golf -->
      <v-tab-item value="golf">
        <!-- KPI Cards (Ultra Low Cost) -->
        <v-row class="mb-6">
          <!-- Jugadores Card -->
          <v-col cols="12" md="12">
            <v-card flat class="pgs-kpi-card pa-5 rounded-xl h-100 d-flex flex-column justify-space-between">
              <div>
                <!-- Header: Title, Icon & Live Pill -->
                <div class="d-flex align-center justify-space-between mb-3">
                  <div class="d-flex align-center">
                    <div class="pgs-kpi-icon-pill pgs-kpi-icon-blue mr-3">
                      <v-icon size="18" color="inherit">mdi-golf-cart</v-icon>
                    </div>
                    <div>
                      <div class="pgs-kpi-label">Jugadores Hoy</div>
                      <div class="text-caption pgs-text-muted line-height-tight">Afluencia y ocupación</div>
                    </div>
                  </div>
                  <div class="pgs-badge-live">
                    <span class="pgs-badge-dot"></span>
                    <span>En vivo</span>
                  </div>
                </div>

                <!-- Main Metric -->
                <div class="d-flex align-baseline justify-space-between my-2">
                  <div class="d-flex align-baseline">
                    <span v-if="loading.departures" class="text-h4 font-weight-bold pgs-text-primary mr-2">
                      <v-progress-circular indeterminate color="primary" size="28" width="3"></v-progress-circular>
                    </span>
                    <template v-else>
                      <span class="pgs-kpi-metric mr-2">{{ stats.players }}</span>
                      <span class="pgs-kpi-unit">Jugadores</span>
                    </template>
                  </div>
                  <div class="text-right">
                    <span class="text-caption font-weight-medium pgs-text-secondary">
                      {{ stats.reservations }} salidas · {{ averagePaxPerFlight }} jugadores/salida
                    </span>
                  </div>
                </div>

                <!-- Progress distribution bar -->
                <div class="pgs-kpi-progress-bar my-3" title="Distribución de jugadores">
                  <div class="pgs-progress-segment bg-success" :style="{ width: paidPlayersPercent + '%' }"></div>
                  <div class="pgs-progress-segment bg-warning" :style="{ width: pendingPlayersPercent + '%' }"></div>
                  <div class="pgs-progress-segment bg-error" :style="{ width: canceledPlayersPercent + '%' }"></div>
                </div>
              </div>

              <!-- Enriched Footer Breakdown -->
              <div class="pgs-kpi-footer pt-3 border-top-light d-flex align-center justify-space-between text-caption">
                <div class="d-flex align-center">
                  <span class="pgs-status-dot bg-success mr-1"></span>
                  <span class="font-weight-medium text-slate-700">{{ stats.paid }}</span>
                  <span class="pgs-text-muted ml-1">Pagados</span>
                </div>
                <div class="d-flex align-center">
                  <span class="pgs-status-dot bg-warning mr-1"></span>
                  <span class="font-weight-medium text-slate-700">{{ stats.pending }}</span>
                  <span class="pgs-text-muted ml-1">Pend.</span>
                </div>
                <div class="d-flex align-center">
                  <span class="pgs-status-dot bg-error mr-1"></span>
                  <span class="font-weight-medium text-slate-700">{{ stats.canceled + stats.noShow }}</span>
                  <span class="pgs-text-muted ml-1">Canc.</span>
                </div>
              </div>
            </v-card>
          </v-col>

          <!-- Ingresos MXN Card -->
          <v-col v-if="false" cols="12" md="4">
            <v-card flat class="pgs-kpi-card pa-5 rounded-xl h-100 d-flex flex-column justify-space-between">
              <div>
                <!-- Header: Title, Icon & Currency Pill -->
                <div class="d-flex align-center justify-space-between mb-3">
                  <div class="d-flex align-center">
                    <div class="pgs-kpi-icon-pill pgs-kpi-icon-emerald mr-3">
                      <v-icon size="18" color="inherit">mdi-cash-multiple</v-icon>
                    </div>
                    <div>
                      <div class="pgs-kpi-label">Ingresos (MXN)</div>
                      <div class="text-caption pgs-text-muted line-height-tight">Total proyectado hoy</div>
                    </div>
                  </div>
                  <div class="pgs-badge-currency">
                    <span>MXN</span>
                  </div>
                </div>

                <!-- Main Metric -->
                <div class="d-flex align-baseline justify-space-between my-2">
                  <div class="d-flex align-baseline">
                    <span v-if="loading.departures" class="text-h4 font-weight-bold pgs-text-primary mr-2">
                      <v-progress-circular indeterminate color="success" size="28" width="3"></v-progress-circular>
                    </span>
                    <template v-else>
                      <span class="pgs-kpi-metric mr-1">${{ formatNumber(stats.revenueMXN) }}</span>
                    </template>
                  </div>
                  <div class="text-right">
                    <span class="text-caption font-weight-medium pgs-text-secondary" v-if="stats.avgTicketMXN > 0">
                      Prom: ${{ formatNumber(stats.avgTicketMXN) }}/jugadores
                    </span>
                    <span class="text-caption font-weight-medium pgs-text-muted" v-else>
                      Sin cobros
                    </span>
                  </div>
                </div>

                <!-- Progress bar: Cobrado vs Pendiente -->
                <div class="pgs-kpi-progress-bar my-3" title="% Cobrado en MXN">
                  <div class="pgs-progress-segment bg-emerald" :style="{ width: paidRevenuePercentMXN + '%' }"></div>
                  <div class="pgs-progress-segment bg-amber-soft"
                    :style="{ width: (100 - paidRevenuePercentMXN) + '%' }"></div>
                </div>
              </div>

              <!-- Enriched Footer Breakdown -->
              <div class="pgs-kpi-footer pt-3 border-top-light d-flex align-center justify-space-between text-caption">
                <div class="d-flex align-center">
                  <v-icon size="14" color="success" class="mr-1">mdi-check-circle-outline</v-icon>
                  <span class="pgs-text-muted mr-1">Cobrado:</span>
                  <span class="font-weight-semibold text-emerald">${{ formatNumber(stats.paidRevenueMXN) }}</span>
                  <span class="pgs-text-muted ml-1" v-if="stats.revenueMXN > 0">({{ paidRevenuePercentMXN }}%)</span>
                </div>
                <div class="d-flex align-center">
                  <v-icon size="14" color="warning" class="mr-1">mdi-clock-outline</v-icon>
                  <span class="pgs-text-muted mr-1">Pendiente:</span>
                  <span class="font-weight-medium pgs-text-secondary">${{ formatNumber(stats.pendingRevenueMXN)
                  }}</span>
                </div>
              </div>
            </v-card>
          </v-col>

          <!-- Ingresos USD Card -->
          <v-col v-if="false" cols="12" md="4">
            <v-card flat class="pgs-kpi-card pa-5 rounded-xl h-100 d-flex flex-column justify-space-between">
              <div>
                <!-- Header: Title, Icon & Currency Pill -->
                <div class="d-flex align-center justify-space-between mb-3">
                  <div class="d-flex align-center">
                    <div class="pgs-kpi-icon-pill pgs-kpi-icon-amber mr-3">
                      <v-icon size="18" color="inherit">mdi-currency-usd</v-icon>
                    </div>
                    <div>
                      <div class="pgs-kpi-label">Ingresos (USD)</div>
                      <div class="text-caption pgs-text-muted line-height-tight">Moneda extranjera</div>
                    </div>
                  </div>
                  <div class="pgs-badge-currency">
                    <span>USD</span>
                  </div>
                </div>

                <!-- Main Metric -->
                <div class="d-flex align-baseline justify-space-between my-2">
                  <div class="d-flex align-baseline">
                    <span v-if="loading.departures" class="text-h4 font-weight-bold pgs-text-primary mr-2">
                      <v-progress-circular indeterminate color="warning" size="28" width="3"></v-progress-circular>
                    </span>
                    <template v-else>
                      <span class="pgs-kpi-metric mr-1">${{ formatNumber(stats.revenueUSD) }}</span>
                    </template>
                  </div>
                  <div class="text-right">
                    <span class="text-caption font-weight-medium pgs-text-secondary" v-if="stats.avgTicketUSD > 0">
                      Prom: ${{ formatNumber(stats.avgTicketUSD) }}/jugadores
                    </span>
                    <span class="text-caption font-weight-medium pgs-text-muted" v-else>
                      Sin cobros
                    </span>
                  </div>
                </div>

                <!-- Progress bar: Cobrado vs Pendiente -->
                <div class="pgs-kpi-progress-bar my-3" title="% Cobrado en USD">
                  <div class="pgs-progress-segment bg-amber" :style="{ width: paidRevenuePercentUSD + '%' }"></div>
                  <div class="pgs-progress-segment bg-slate-soft"
                    :style="{ width: (100 - paidRevenuePercentUSD) + '%' }"></div>
                </div>
              </div>

              <!-- Enriched Footer Breakdown -->
              <div class="pgs-kpi-footer pt-3 border-top-light d-flex align-center justify-space-between text-caption">
                <div class="d-flex align-center">
                  <v-icon size="14" color="warning" class="mr-1">mdi-check-circle-outline</v-icon>
                  <span class="pgs-text-muted mr-1">Cobrado:</span>
                  <span class="font-weight-semibold text-amber">${{ formatNumber(stats.paidRevenueUSD) }}</span>
                  <span class="pgs-text-muted ml-1" v-if="stats.revenueUSD > 0">({{ paidRevenuePercentUSD }}%)</span>
                </div>
                <div class="d-flex align-center">
                  <v-icon size="14" color="grey" class="mr-1">mdi-clock-outline</v-icon>
                  <span class="pgs-text-muted mr-1">Pendiente:</span>
                  <span class="font-weight-medium pgs-text-secondary">${{ formatNumber(stats.pendingRevenueUSD)
                  }}</span>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Gráficas FusionCharts -->
        <!-- AI Assistant Prompt Chat Section -->
        <ai-prompt-chat :endpoint="aiEndpoint"
          :context-data="{ stats, weather, departuresCount: nextDepartures.length, date: currentDate }"></ai-prompt-chat>

        <!-- Gráficas FusionCharts -->
        <v-row class="mb-6">
          <v-col cols="12" md="6">
            <v-card flat class="pgs-dashboard-panel rounded-xl h-100 pa-4">
              <div v-if="loading.departures" class="d-flex justify-center align-center h-100 min-h-300">
                <v-progress-circular indeterminate color="primary" size="48" width="4"></v-progress-circular>
              </div>
              <fusioncharts v-else type="spline" width="100%" height="350" dataFormat="json"
                :dataSource="chartDataTime">
              </fusioncharts>
            </v-card>
          </v-col>

          <v-col cols="12" md="6">
            <v-card flat class="pgs-dashboard-panel rounded-xl h-100 pa-4">
              <div v-if="loading.departures" class="d-flex justify-center align-center h-100 min-h-300">
                <v-progress-circular indeterminate color="primary" size="48" width="4"></v-progress-circular>
              </div>
              <fusioncharts v-else type="pie2d" width="100%" height="350" dataFormat="json"
                :dataSource="chartDataStatus">
              </fusioncharts>
            </v-card>
          </v-col>
        </v-row>

        <!-- Gráfica Predictiva de Tráfico (Heatmap) -->
        <v-row class="mb-6">
          <v-col cols="12">
            <v-card flat class="pgs-dashboard-panel rounded-xl h-100 pa-4">
              <v-alert dense type="info" text color="primary" class="mb-4 text-body-2 text-left rounded-lg">
                Esta gráfica de tráfico es una <strong>estimación predictiva</strong> basada en las horas de
                salida
                (Tee Times) y el tamaño de los grupos. La ubicación real de los jugadores en el campo puede variar.
                <div class="mt-1 text-caption pgs-text-secondary">
                  <strong>Modelo de ritmo de juego utilizado:</strong> 1-2 Pax = 10 mins/hoyo &nbsp;|&nbsp; 3 Pax = 12
                  mins/hoyo
                  &nbsp;|&nbsp; 4 Pax = 14 mins/hoyo &nbsp;|&nbsp; 5+ Pax = 16 mins/hoyo.
                </div>
              </v-alert>
              <div v-if="loading.departures" class="d-flex justify-center align-center h-100 min-h-300">
                <v-progress-circular indeterminate color="primary" size="48" width="4"></v-progress-circular>
              </div>
              <fusioncharts v-else type="heatmap" width="100%" height="450" dataFormat="json"
                :dataSource="chartDataTraffic">
              </fusioncharts>
            </v-card>
          </v-col>
        </v-row>

        <!-- Próximas Salidas (Tee Sheet) y Accesos Rápidos -->
        <v-row>
          <!-- Próximas Salidas Table -->
          <v-col cols="12" md="12" lg="12">
            <v-card flat class="pgs-dashboard-panel rounded-xl h-100">
              <div class="pa-5 d-flex align-center justify-space-between border-bottom-light">
                <div class="d-flex align-center">
                  <v-icon color="primary" class="mr-3">mdi-golf-tee</v-icon>
                  <div>
                    <h2 class="text-h6 font-weight-bold pgs-text-primary mb-0">Próximas Salidas</h2>
                    <span class="text-caption pgs-text-secondary">Salidas programadas para el día de hoy</span>
                  </div>
                </div>
                <v-btn text color="primary" small class="text-none font-weight-medium" @click="goRoute('/booking')">
                  Ver Tee Sheet <v-icon right size="16">mdi-arrow-right</v-icon>
                </v-btn>
              </div>

              <v-data-table :headers="departuresHeaders" :items="nextDepartures" :loading="loading.departures"
                :items-per-page="5" class="pgs-custom-table" no-data-text="No hay salidas registradas para hoy.">
                <template v-slot:item.time="{ item }">
                  <div class="d-flex align-center">
                    <v-icon size="16" color="primary" class="mr-1">mdi-clock-outline</v-icon>
                    <span class="font-weight-bold" style="color: #0F172A;">{{ item.time }}</span>
                  </div>
                </template>
                <template v-slot:item.client="{ item }">
                  <div class="d-flex align-center py-2">
                    <v-avatar size="32" color="#EFF6FF" class="mr-3">
                      <span class="font-weight-bold" style="color: #3B82F6; font-size: 12px;">
                        {{ item.client ? item.client.charAt(0).toUpperCase() : 'U' }}
                      </span>
                    </v-avatar>
                    <div>
                      <div class="font-weight-medium text-body-2" style="color: #0F172A;">{{ item.client }}</div>
                      <div v-if="item.folio" class="text-caption pgs-text-secondary">Folio: {{ item.folio }}</div>
                    </div>
                  </div>
                </template>
                <template v-slot:item.tarifa="{ item }">
                  <v-chip v-if="item.tarifa" x-small color="#F1F5F9" text-color="#475569"
                    class="font-weight-medium px-2 rounded-md">
                    {{ item.tarifa }}
                  </v-chip>
                  <span v-else class="text-caption text-muted">--</span>
                </template>
                <template v-slot:item.pax="{ item }">
                  <v-chip small color="#F1F5F9" text-color="#475569" class="font-weight-medium px-3 rounded-lg">
                    <v-icon left size="14">mdi-account-group</v-icon> {{ item.pax }} Jugadores
                  </v-chip>
                </template>
                <template v-slot:item.status="{ item }">
                  <v-chip small :color="getStatusColor(item.status)" text-color="white"
                    class="font-weight-medium px-3 rounded-lg">
                    {{ item.status }}
                  </v-chip>
                </template>
              </v-data-table>
            </v-card>
          </v-col>

          <!-- Quick Actions -->

        </v-row>
      </v-tab-item>

      <!-- Tab Actividades -->
      <v-tab-item value="actividades" class="pt-4">
        <HomeActividades />
      </v-tab-item>

      <!-- Tab A&B -->
      <v-tab-item value="ab" class="pt-4">
        <HomeAYB />
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
import moment from "moment";
import { mapGetters } from "vuex";
import Reserva from "../../clases/Reserva.js";
import AiPromptChat from "../AI/AiPromptChat.vue";
import HomeActividades from "./HomeActividades.vue";
import HomeAYB from "./HomeAYB.vue";

export default {
  name: "Home",
  components: {
    AiPromptChat,
    HomeActividades,
    HomeAYB
  },
  data() {
    return {
      activeTab: 'golf',
      aiEndpoint: "",
      stats: {
        reservations: 0,
        players: 0,
        totalRegistered: 0,
        paid: 0,
        pending: 0,
        canceled: 0,
        noShow: 0,
        revenueMXN: 0,
        paidRevenueMXN: 0,
        pendingRevenueMXN: 0,
        avgTicketMXN: 0,
        revenueUSD: 0,
        paidRevenueUSD: 0,
        pendingRevenueUSD: 0,
        avgTicketUSD: 0
      },
      chartDataTime: {
        chart: {
          caption: "Flujo de Jugadores por Hora",
          xAxisName: "Hora",
          yAxisName: "Jugadores (Pax)",
          theme: "fusion",
          bgColor: "#ffffff",
          showBorder: "0",
          palettecolors: "#3B82F6",
          drawAnchors: "1",
          anchorRadius: "4",
          anchorBgColor: "#3B82F6"
        },
        data: []
      },
      chartDataStatus: {
        chart: {
          caption: "Distribución por Estatus (Jugadores)",
          theme: "fusion",
          bgColor: "#ffffff",
          showBorder: "0",
          enableSmartLabels: "1",
          showPercentValues: "1",
          decimals: "1",
          palettecolors: "#10B981,#F59E0B,#EF4444,#64748B"
        },
        data: []
      },
      chartDataTraffic: {
        chart: {
          caption: "Predicción de Tráfico en Campo ",
          subcaption: "Basado en ritmo de juego promedio",
          xAxisName: "Hora (Bloques de 30 mins)",
          yAxisName: "Hoyo",
          showValues: "0",
          theme: "fusion",
          showplotborder: "1",
          plottooltext: "Hora: $columnlabel<br>Hoyo: $rowlabel<br>Pax Estimados: $dataValue"
        },
        rows: { row: [] },
        columns: { column: [] },
        dataset: [{ data: [] }],
        colorrange: {
          gradient: "0",
          color: [
            { code: "#10B981", minvalue: "0", maxvalue: "4", label: "Fluido" },
            { code: "#FBBF24", minvalue: "5", maxvalue: "8", label: "Tráfico Ligero" },
            { code: "#EF4444", minvalue: "9", maxvalue: "100", label: "Cuello de Botella" }
          ]
        }
      },
      MainDepartamento: "Golf",
      actualListener: null,
      unsubscribeReservas: null,
      loading: {
        departures: true
      },
      nextDepartures: [],
      departuresHeaders: [
        { text: 'Hora', value: 'time', sortable: true, width: '110px' },
        { text: 'Cliente', value: 'client', sortable: true },
        { text: 'Tarifa', value: 'tarifa', sortable: false },
        { text: 'Pax', value: 'pax', sortable: false, align: 'center' },
        { text: 'Estatus', value: 'status', sortable: false, align: 'end' }
      ],
      quickActions: [
        { title: 'Punto de Venta', icon: 'mdi-cash-register', color: '#10B981', route: '/PDV' },
        { title: 'Tee Sheet', icon: 'mdi-golf', color: '#3B82F6', route: '/tee_sheet' },
        { title: 'Clientes', icon: 'mdi-account-multiple', color: '#8B5CF6', route: '/clientes' },
        { title: 'Reportes', icon: 'mdi-chart-box-outline', color: '#F59E0B', route: '/reportes' }
      ],
      weather: null
    };
  },
  computed: {
    ...mapGetters(["getFirebaseApp"]),
    ...mapGetters(["getSalidas"]),
    currentDate() {
      moment.locale('es');
      return moment().format('dddd, D [de] MMMM YYYY');
    },
    tenant() {
      return this.getFirebaseApp.tenant || "";
    },
    weatherIcon() {
      if (!this.weather) return 'mdi-weather-cloudy';
      const code = this.weather.weathercode;
      if (code === 0) return 'mdi-weather-sunny';
      if (code >= 1 && code <= 3) return 'mdi-weather-partly-cloudy';
      if (code >= 51 && code <= 67) return 'mdi-weather-rainy';
      if (code >= 71 && code <= 77) return 'mdi-weather-snowy';
      if (code >= 95) return 'mdi-weather-lightning';
      return 'mdi-weather-cloudy';
    },
    weatherIconColor() {
      if (!this.weather) return '#94A3B8';
      const code = this.weather.weathercode;
      if (code === 0) return '#F59E0B'; // Sunny
      if (code >= 1 && code <= 3) return '#94A3B8'; // Cloudy
      if (code >= 51 && code <= 67) return '#3B82F6'; // Rain
      return '#64748B';
    },
    weatherDescription() {
      if (!this.weather) return '';
      const code = this.weather.weathercode;
      if (code === 0) return 'Despejado';
      if (code >= 1 && code <= 3) return 'Parcialmente nublado';
      if (code >= 51 && code <= 67) return 'Lluvia';
      if (code >= 95) return 'Tormenta';
      return 'Nublado';
    },
    averagePaxPerFlight() {
      if (!this.stats.reservations || this.stats.reservations === 0) return "0.0";
      return (this.stats.players / this.stats.reservations).toFixed(1);
    },
    paidPlayersPercent() {
      const total = this.stats.totalRegistered || this.stats.players;
      if (!total || total === 0) return 0;
      return Math.min(100, Math.round((this.stats.paid / total) * 100));
    },
    pendingPlayersPercent() {
      const total = this.stats.totalRegistered || this.stats.players;
      if (!total || total === 0) return 0;
      return Math.min(100, Math.round((this.stats.pending / total) * 100));
    },
    canceledPlayersPercent() {
      const total = this.stats.totalRegistered || this.stats.players;
      if (!total || total === 0) return 0;
      return Math.min(100, Math.round(((this.stats.canceled + this.stats.noShow) / total) * 100));
    },
    paidRevenuePercentMXN() {
      if (!this.stats.revenueMXN || this.stats.revenueMXN === 0) return 0;
      return Math.min(100, Math.round((this.stats.paidRevenueMXN / this.stats.revenueMXN) * 100));
    },
    paidRevenuePercentUSD() {
      if (!this.stats.revenueUSD || this.stats.revenueUSD === 0) return 0;
      return Math.min(100, Math.round((this.stats.paidRevenueUSD / this.stats.revenueUSD) * 100));
    }
  },
  methods: {
    goRoute(route) {
      this.$router.push({ path: route }).catch(() => { });
    },
    getStatusColor(status) {
      const s = status ? status.toLowerCase() : '';
      if (s.includes('confirm') || s.includes('pagad')) return '#10B981';
      if (s.includes('pend') || s.includes('reserv')) return '#F59E0B';
      if (s.includes('cancel')) return '#EF4444';
      if (s.includes('checkin') || s.includes('llegó')) return '#3B82F6';
      return '#64748B';
    },
    formatNumber(num) {
      return Number(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    async fetchWeather() {
      try {
        // Coordenadas genericas iniciales (Cancun). Usamos API gratuita de Open-Meteo.
        const res = await fetch('https://api.open-meteo.com/v1/forecast?latitude=21.1619&longitude=-86.8515&current_weather=true');
        const data = await res.json();
        this.weather = data.current_weather;
      } catch (error) {
        console.error("Error fetching weather:", error);
      }
    },

    calculateTrafficModel(departures) {
      // 1. Setup rows (Hoyos 1 to 18)
      let rows = [];
      for (let i = 1; i <= 18; i++) {
        rows.push({ id: i.toString(), label: `Hoyo ${i}` });
      }

      // 2. Setup columns (Horas, e.g. 06:00 to 19:00, every 30 mins)
      let columns = [];
      let startHour = 6;
      let endHour = 19;
      for (let h = startHour; h <= endHour; h++) {
        const hStr = h.toString().padStart(2, '0');
        columns.push({ id: `${hStr}:00`, label: `${hStr}:00` });
        columns.push({ id: `${hStr}:30`, label: `${hStr}:30` });
      }

      // 3. Initialize grid
      let grid = {}; // grid[hoyoId][horaId] = paxCount
      rows.forEach(r => {
        grid[r.id] = {};
        columns.forEach(c => {
          grid[r.id][c.id] = 0;
        });
      });

      // 4. Proyectar tiempos
      departures.forEach(dep => {
        if (!dep.time || dep.time === '--:--' || dep.pax === 0 || (dep.status.toLowerCase().includes('cancel'))) return;

        let pax = dep.pax;
        let minsPerHole = 14; // Default para 4 pax
        if (pax <= 2) minsPerHole = 10;
        else if (pax === 3) minsPerHole = 12;
        else if (pax >= 5) minsPerHole = 16;

        let currentMoment = moment(dep.time, "HH:mm");

        for (let i = 1; i <= 18; i++) {
          let holeStr = i.toString();

          let hStr = currentMoment.format("HH");
          let mStr = currentMoment.minutes() < 30 ? "00" : "30";
          let blockId = `${hStr}:${mStr}`;

          if (grid[holeStr] && grid[holeStr][blockId] !== undefined) {
            grid[holeStr][blockId] += pax;
          }

          currentMoment.add(minsPerHole, 'minutes');
        }
      });

      // 5. Build dataset
      let dataPoints = [];
      rows.forEach(r => {
        columns.forEach(c => {
          dataPoints.push({
            rowid: r.id,
            columnid: c.id,
            value: grid[r.id][c.id].toString()
          });
        });
      });

      this.chartDataTraffic.rows.row = rows;
      this.chartDataTraffic.columns.column = columns;
      this.chartDataTraffic.dataset[0].data = dataPoints;
    },

    killListener(date) {
      if (typeof this.unsubscribeReservas === "function") {
        try {
          this.unsubscribeReservas();
        } catch (e) {
          console.warn("Error calling unsubscribe function:", e);
        }
        this.unsubscribeReservas = null;
      }
      if (date && this.getFirebaseApp && this.getFirebaseApp.database) {
        try {
          const path = "RVA/Reservas/" + (this.MainDepartamento || "Golf") + "/" + date;
          const ref = this.getFirebaseApp.database.ref(path);
          this.getFirebaseApp.database.off(ref);
        } catch (e) {
          console.warn("Error calling database.off:", e);
        }
      }
    },

    fetchReservasRTDB() {
      const date = moment().format("YYYY-MM-DD");

      if (this.actualListener) {
        this.killListener(this.actualListener);
      }

      this.loading.departures = true;

      this.unsubscribeReservas = this.getFirebaseApp.database.queryBuilderListener(
        "RVA/Reservas/" + (this.MainDepartamento || "Golf") + "/" + date,
        null,
        (snapshot) => {
          try {
            if (snapshot.exists()) {
              let arrayReservas = snapshot.val();

              let reservaList = Reserva.parseAvailabilityData(arrayReservas, "ALL", this.getSalidas);

              let totalPlayers = 0;
              let totalRegistered = 0;
              let totalReservas = 0;
              let totalPagados = 0;
              let totalPendientes = 0;
              let totalCancelados = 0;
              let totalNoShow = 0;
              let revMXN = 0;
              let paidRevMXN = 0;
              let pendingRevMXN = 0;
              let paidPlayersMXN = 0;

              let revUSD = 0;
              let paidRevUSD = 0;
              let pendingRevUSD = 0;
              let paidPlayersUSD = 0;

              let departures = [];
              let timeDataMap = {}; // Para agrupar jugadores por hora

              reservaList.forEach((element) => {
                const rawTime = element.time || element.Hora || '';
                const hourLabel = rawTime ? moment(rawTime, "HH:mm:ss").format("HH:mm") : '--:--';

                if (!timeDataMap[hourLabel]) {
                  timeDataMap[hourLabel] = 0;
                }

                // Métricas generales
                let totalInSlot = (element.rvsData ? element.rvsData.length : 0);
                totalRegistered += totalInSlot;

                let activePlayersInSlot = 0;
                if (element.rvsData) {
                  element.rvsData.forEach((ply) => {
                    if (ply.E !== "C" && ply.E !== "N") {
                      activePlayersInSlot++;
                    }
                  });
                }

                totalPlayers += activePlayersInSlot;
                timeDataMap[hourLabel] += activePlayersInSlot;

                if (element.Rvs) {
                  totalReservas += Object.keys(element.Rvs).length;

                  // Extraer próximas salidas
                  Object.keys(element.Rvs).forEach(resKey => {
                    const r = element.Rvs[resKey];
                    const timeSlot = hourLabel;

                    let clientName = 'Sin nombre';
                    let pax = 1;
                    let status = 'Confirmada';
                    let tarifa = '';
                    let folio = r.frva || '';

                    if (r.Pys && r.Pys.length > 0) {
                      clientName = r.Pys[0].Nom || 'Sin nombre';
                      pax = r.Pys.length;
                      tarifa = r.Pys[0].trfr || '';
                      if (!folio && r.Pys[0].FolioReserva) folio = r.Pys[0].FolioReserva;

                      const statusCode = r.Pys[0].E;
                      if (statusCode === 'P') status = 'Pagada';
                      else if (statusCode === 'R') status = 'Pendiente';
                      else if (statusCode === 'C') status = 'Cancelada';
                      else if (statusCode === 'N') status = 'No Show';
                    }

                    departures.push({
                      id: resKey,
                      time: timeSlot,
                      client: clientName,
                      pax: pax,
                      status: status,
                      tarifa: tarifa,
                      folio: folio
                    });
                  });
                }

                // Desglose de estados de jugadores e ingresos
                if (element.rvsData) {
                  element.rvsData.forEach((ply) => {
                    // Contar estado
                    if (ply.E) {
                      if (ply.E == "P") totalPagados++;
                      else if (ply.E == "R") totalPendientes++;
                      else if (ply.E == "C") totalCancelados++;
                      else if (ply.E == "N") totalNoShow++;
                    }

                    // Sumar precio si existe y NO está cancelado
                    if (ply.prco && ply.E !== "C" && ply.E !== "N") {
                      const amount = Number(ply.prco.C) || 0;
                      const currency = ply.prco.M ? String(ply.prco.M).toUpperCase() : "MXN";
                      if (currency === "MXN") {
                        revMXN += amount;
                        if (ply.E === "P") {
                          paidRevMXN += amount;
                          paidPlayersMXN++;
                        } else if (ply.E === "R") {
                          pendingRevMXN += amount;
                        }
                      } else if (currency === "USD") {
                        revUSD += amount;
                        if (ply.E === "P") {
                          paidRevUSD += amount;
                          paidPlayersUSD++;
                        } else if (ply.E === "R") {
                          pendingRevUSD += amount;
                        }
                      }
                    }
                  });
                }
              });

              // Calcular Modelo Predictivo
              this.calculateTrafficModel(departures);

              this.stats.players = totalPlayers;
              this.stats.totalRegistered = totalRegistered;
              this.stats.reservations = totalReservas;
              this.stats.paid = totalPagados;
              this.stats.pending = totalPendientes;
              this.stats.canceled = totalCancelados;
              this.stats.noShow = totalNoShow;
              this.stats.revenueMXN = revMXN;
              this.stats.paidRevenueMXN = paidRevMXN;
              this.stats.pendingRevenueMXN = pendingRevMXN;
              this.stats.avgTicketMXN = paidPlayersMXN > 0 ? (paidRevMXN / paidPlayersMXN) : (totalPlayers > 0 && revMXN > 0 ? (revMXN / totalPlayers) : 0);
              this.stats.revenueUSD = revUSD;
              this.stats.paidRevenueUSD = paidRevUSD;
              this.stats.pendingRevenueUSD = pendingRevUSD;
              this.stats.avgTicketUSD = paidPlayersUSD > 0 ? (paidRevUSD / paidPlayersUSD) : (totalPlayers > 0 && revUSD > 0 ? (revUSD / totalPlayers) : 0);

              // Generar datos para la gráfica de Tiempo (Line Chart)
              let timeChartArray = [];
              Object.keys(timeDataMap).sort().forEach(timeKey => {
                if (timeKey !== '--:--') {
                  timeChartArray.push({
                    label: timeKey,
                    value: timeDataMap[timeKey]
                  });
                }
              });
              this.chartDataTime.data = timeChartArray;

              // Generar datos para la gráfica de Estatus (Pie Chart)
              this.chartDataStatus.data = [
                { label: "Pagados", value: totalPagados },
                { label: "Pendientes", value: totalPendientes },
                { label: "Cancelados", value: totalCancelados }
              ].filter(item => item.value > 0);

              // Ordenar y filtrar próximas salidas
              departures.sort((a, b) => moment(a.time, "HH:mm").diff(moment(b.time, "HH:mm")));
              const now = moment();
              const upcoming = departures.filter(d => d.time !== '--:--' && moment(d.time, "HH:mm").isSameOrAfter(now, 'minute'));

              // Si hay salidas próximas después de la hora actual, mostrar esas; de lo contrario mostrar las del día
              this.nextDepartures = upcoming.length > 0 ? upcoming : departures;

            } else {
              this.stats.players = 0;
              this.stats.totalRegistered = 0;
              this.stats.reservations = 0;
              this.stats.paid = 0;
              this.stats.pending = 0;
              this.stats.canceled = 0;
              this.stats.noShow = 0;
              this.stats.revenueMXN = 0;
              this.stats.paidRevenueMXN = 0;
              this.stats.pendingRevenueMXN = 0;
              this.stats.avgTicketMXN = 0;
              this.stats.revenueUSD = 0;
              this.stats.paidRevenueUSD = 0;
              this.stats.pendingRevenueUSD = 0;
              this.stats.avgTicketUSD = 0;
              this.nextDepartures = [];
              this.chartDataTime.data = [];
              this.chartDataStatus.data = [];
            }
          } catch (e) {
            console.error("Error procesando reservas desde RTDB:", e);
          } finally {
            this.loading.departures = false;
          }
        }
      );
      this.actualListener = date;
    }
  },
  mounted() {
    this.MainDepartamento = localStorage.getItem("selectedDepartment") || "Golf";
    this.fetchWeather();
    setTimeout(() => {
      this.fetchReservasRTDB();
    }, 500);
  },
  beforeDestroy() {
    if (this.actualListener) {
      this.killListener(this.actualListener);
    }
  },
  destroyed() {
    if (this.actualListener) {
      this.killListener(this.actualListener);
    }
  }
};
</script>

<style scoped>
.pgs-dashboard {
  font-family: 'Inter', -apple-system, sans-serif;
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px;
}

.pgs-text-primary {
  color: #0F172A;
}

.pgs-text-secondary {
  color: #64748B;
}

.letter-spacing-wide {
  letter-spacing: 0.5px;
}

.line-height-tight {
  line-height: 1.2;
}

.cursor-pointer {
  cursor: pointer;
}

.border-bottom-light {
  border-bottom: 1px solid #F1F5F9;
}

.pgs-weather-pill {
  background-color: #FFFFFF;
  border: 1px solid #E2E8F0;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.pgs-kpi-card {
  background: #FFFFFF !important;
  border: 1px solid #E2E8F0 !important;
  border-radius: 16px !important;
  box-shadow: 0 1px 3px 0 rgba(15, 23, 42, 0.04), 0 1px 2px -1px rgba(15, 23, 42, 0.04) !important;
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.2s ease;
}

.pgs-kpi-card:hover {
  transform: translateY(-2px);
  border-color: #CBD5E1 !important;
  box-shadow: 0 10px 20px -5px rgba(15, 23, 42, 0.08), 0 4px 6px -2px rgba(15, 23, 42, 0.04) !important;
}

.pgs-kpi-label {
  font-size: 0.8125rem;
  font-weight: 700;
  color: #1E293B;
  letter-spacing: 0.2px;
}

.pgs-kpi-metric {
  font-size: 1.85rem;
  font-weight: 800;
  color: #0F172A;
  letter-spacing: -0.03em;
  line-height: 1.1;
}

.pgs-kpi-unit {
  font-size: 0.875rem;
  font-weight: 600;
  color: #64748B;
}

.pgs-kpi-icon-pill {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.pgs-kpi-icon-blue {
  background-color: #EFF6FF;
  color: #2563EB;
}

.pgs-kpi-icon-emerald {
  background-color: #ECFDF5;
  color: #059669;
}

.pgs-kpi-icon-amber {
  background-color: #FFFBEB;
  color: #D97706;
}

.pgs-badge-live {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 8px;
  border-radius: 9999px;
  background: #F0FDF4;
  border: 1px solid #DCFCE7;
  color: #16A34A;
  font-size: 11px;
  font-weight: 600;
}

.pgs-badge-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #22C55E;
  box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.2);
  animation: pgsPulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pgsPulse {

  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }

  50% {
    opacity: 0.4;
    transform: scale(1.15);
  }
}

.pgs-badge-currency {
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: 9999px;
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  color: #475569;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.pgs-kpi-progress-bar {
  width: 100%;
  height: 5px;
  background-color: #F1F5F9;
  border-radius: 9999px;
  display: flex;
  overflow: hidden;
}

.pgs-progress-segment {
  height: 100%;
  transition: width 0.4s ease;
}

.bg-emerald {
  background-color: #10B981 !important;
}

.bg-amber {
  background-color: #F59E0B !important;
}

.bg-amber-soft {
  background-color: #FEF3C7 !important;
}

.bg-slate-soft {
  background-color: #E2E8F0 !important;
}

.bg-success {
  background-color: #10B981 !important;
}

.bg-warning {
  background-color: #F59E0B !important;
}

.bg-error {
  background-color: #EF4444 !important;
}

.pgs-status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
}

.pgs-text-muted {
  color: #94A3B8;
}

.text-slate-700 {
  color: #334155;
}

.text-emerald {
  color: #059669;
}

.text-amber {
  color: #D97706;
}

.border-top-light {
  border-top: 1px solid #F1F5F9;
}

.pgs-dashboard-panel {
  border: 1px solid #E2E8F0 !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.03), 0 2px 4px -2px rgba(0, 0, 0, 0.03) !important;
  overflow: hidden;
}

.pgs-action-card {
  border: 1px solid #F1F5F9 !important;
  background-color: #F8FAFC !important;
  transition: all 0.2s ease;
}

.pgs-action-card:hover {
  background-color: #FFFFFF !important;
  border-color: #E2E8F0 !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05) !important;
  transform: translateY(-2px);
}

.min-h-300 {
  min-height: 300px;
}

.pgs-custom-table {
  background: transparent !important;
}

.pgs-custom-table>>>.v-data-table-header th {
  background-color: #F8FAFC !important;
  color: #64748B !important;
  font-weight: 600 !important;
  text-transform: uppercase;
  font-size: 11px !important;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #E2E8F0 !important;
}

.pgs-custom-table>>>tbody tr {
  transition: background-color 0.15s ease;
}

.pgs-custom-table>>>tbody tr:hover {
  background-color: #F8FAFC !important;
}

.pgs-custom-table>>>tbody td {
  border-bottom: 1px solid #F1F5F9 !important;
  padding-top: 12px !important;
  padding-bottom: 12px !important;
}
</style>
